<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useElementSize } from '@vueuse/core';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import ContactAPI from 'dashboard/api/contacts';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import MessageList from 'dashboard/components-next/message/MessageList.vue';
import ConversationChannelChip from './ConversationChannelChip.vue';
import TimelineComposer from './TimelineComposer.vue';

const props = defineProps({
  contact: { type: Object, required: true },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

// Mirrors Api::V1::Accounts::Contacts::MessagesController::RESULTS_PER_PAGE
const MESSAGES_PER_PAGE = 30;
const LOAD_OLDER_THRESHOLD = 100;

const timelineRef = ref(null);
const { height: containerHeight } = useElementSize(timelineRef);

const messages = ref([]);
const topMessageId = ref(null);
const meta = ref({});
const isFetching = ref(false);
const hasOlderMessages = ref(false);
const isPinnedToBottom = ref(true);
const listRef = ref(null);

const currentUserId = useMapGetter('getCurrentUserID');

const subtitle = computed(() => {
  if (!meta.value.conversations_count) return '';
  return [
    t('CONVERSATION_HISTORY.SUBTITLE_CONVERSATIONS', {
      count: meta.value.conversations_count,
    }),
    t('CONVERSATION_HISTORY.SUBTITLE_INBOXES', {
      count: meta.value.inboxes_count,
    }),
  ].join(' · ');
});

const scroller = computed(() => listRef.value?.$el);

const messagesById = computed(
  () => new Map(messages.value.map(message => [message.id, message]))
);

// The channel of the message currently at the top of the viewport, so the agent always knows
// which conversation they are reading even in the middle of a long run of messages.
const topMessage = computed(() => messagesById.value.get(topMessageId.value));

// Message rows are in document order, so their offsets grow monotonically: binary search the
// first one still on screen instead of measuring every row on each scroll frame.
const trackTopMessage = () => {
  const list = scroller.value;
  if (!list) return;

  const nodes = list.querySelectorAll('[data-message-id]');
  const listTop = list.getBoundingClientRect().top;
  let low = 0;
  let high = nodes.length - 1;
  let found = null;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    if (nodes[mid].getBoundingClientRect().bottom > listTop) {
      found = nodes[mid];
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }

  topMessageId.value = found ? Number(found.dataset.messageId) : null;
};

// The timeline mixes conversations, so a message opens a thread wherever its own starts.
const conversationStarts = computed(() => {
  const starts = new Set();
  messages.value.forEach((message, index) => {
    const previous = messages.value[index - 1];
    if (previous?.conversation_id !== message.conversation_id) {
      starts.add(message.id);
    }
  });
  return starts;
});

const scrollToBottom = () => {
  if (scroller.value) scroller.value.scrollTop = scroller.value.scrollHeight;
};

// Right after opening, both the list's own box (the panel is still laying out) and its content
// (media only gets its real height once loaded) keep changing, which would leave the newest
// messages below the fold. Re-pin on either until the agent scrolls up themselves.
// Neither media event bubbles, hence the capture listeners on the scroll container.
const MEDIA_LOAD_EVENTS = ['load', 'loadedmetadata'];

let resizeObserver = null;

const pinToBottom = () => {
  if (isPinnedToBottom.value) scrollToBottom();
  trackTopMessage();
};

const releaseList = list => {
  MEDIA_LOAD_EVENTS.forEach(event =>
    list?.removeEventListener(event, pinToBottom, true)
  );
  resizeObserver?.disconnect();
  resizeObserver = null;
};

watch(scroller, (list, previous) => {
  releaseList(previous);
  if (!list) return;

  MEDIA_LOAD_EVENTS.forEach(event =>
    list.addEventListener(event, pinToBottom, true)
  );
  resizeObserver = new ResizeObserver(pinToBottom);
  resizeObserver.observe(list);
});

onBeforeUnmount(() => releaseList(scroller.value));

const fetchMessages = async () => {
  isFetching.value = true;
  const before = messages.value[0]?.id;
  const heightBeforeLoad = scroller.value?.scrollHeight ?? 0;
  const scrollTopBeforeLoad = scroller.value?.scrollTop ?? 0;
  try {
    const { data } = await ContactAPI.getMessages(props.contact.id, { before });
    messages.value = [...data.payload, ...messages.value];
    meta.value = data.meta;
    hasOlderMessages.value = data.payload.length >= MESSAGES_PER_PAGE;
    await nextTick();
    if (before) {
      // Keep the message the agent was reading in place while older ones are prepended.
      scroller.value.scrollTop =
        scrollTopBeforeLoad + (scroller.value.scrollHeight - heightBeforeLoad);
    } else {
      scrollToBottom();
    }
    // Measuring only makes sense once the browser has laid the new rows out.
    requestAnimationFrame(trackTopMessage);
  } catch (error) {
    useAlert(t('CONVERSATION_HISTORY.MESSAGES_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const onScroll = event => {
  const { scrollTop, scrollHeight, clientHeight } = event.target;
  isPinnedToBottom.value = scrollTop + clientHeight >= scrollHeight - 24;
  trackTopMessage();

  if (isFetching.value || !hasOlderMessages.value) return;
  if (scrollTop < LOAD_OLDER_THRESHOLD) fetchMessages();
};

const lastMessageInboxId = computed(
  () => messages.value[messages.value.length - 1]?.inbox_id ?? null
);

// The composer replies through the store, so anything it adds to the channel's conversation -
// a sent reply, or a message arriving live - has to be mirrored back into the merged timeline.
const activeConversation = ref(null);

const appendNewMessages = async conversationMessages => {
  const newest = messages.value[messages.value.length - 1];
  const known = new Set(messages.value.map(message => message.id));
  const additions = conversationMessages.filter(
    message =>
      Number.isInteger(message.id) &&
      !known.has(message.id) &&
      (!newest ||
        message.created_at > newest.created_at ||
        (message.created_at === newest.created_at && message.id > newest.id))
  );
  if (!additions.length) return;

  messages.value.push(...additions);
  isPinnedToBottom.value = true;
  await nextTick();
  scrollToBottom();
  trackTopMessage();
};

watch(
  () => activeConversation.value?.messages,
  conversationMessages => {
    if (conversationMessages) appendNewMessages(conversationMessages);
  },
  { deep: true }
);

const openConversation = conversationId => {
  emit('close');
  router.push({
    path: frontendURL(
      conversationUrl({ accountId: route.params.accountId, id: conversationId })
    ),
  });
};

onMounted(fetchMessages);
</script>

<template>
  <div ref="timelineRef" class="flex flex-col flex-grow h-full min-w-0 m-0">
    <div
      class="flex items-center flex-shrink-0 gap-3 py-2 border-b border-n-weak bg-n-solid-2 ltr:pl-4 rtl:pr-4 ltr:pr-16 rtl:pl-16"
    >
      <Button
        sm
        slate
        faded
        icon="i-lucide-arrow-left"
        :label="t('CONVERSATION_HISTORY.BACK_TO_CONVERSATION')"
        class="flex-shrink-0"
        @click="emit('close')"
      />
      <span class="flex-shrink-0 size-4 text-n-brand i-lucide-history" />
      <div class="flex flex-col min-w-0">
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{ t('CONVERSATION_HISTORY.TITLE', { name: contact.name }) }}
        </span>
        <span v-if="subtitle" class="text-xs text-n-slate-11">
          {{ subtitle }}
        </span>
      </div>
    </div>
    <div class="relative flex flex-col flex-1 min-h-0">
      <MessageList
        ref="listRef"
        class="relative flex flex-col flex-grow flex-shrink h-full pb-4 m-0 overflow-y-auto basis-px"
        :current-user-id="currentUserId"
        :is-group-conversation="
          Boolean(contact.additional_attributes?.is_group)
        "
        :messages="messages"
        @scroll="onScroll"
      >
        <template #beforeAll>
          <li class="flex justify-center list-none first:mt-auto">
            <Spinner v-if="isFetching" class="my-4 text-n-brand" />
            <span
              v-else-if="!messages.length"
              class="py-10 text-sm text-n-slate-11"
            >
              {{ t('CONVERSATION_HISTORY.EMPTY_STATE') }}
            </span>
          </li>
        </template>
        <template #beforeMessage="{ message }">
          <li
            v-if="conversationStarts.has(message.id)"
            class="flex justify-center py-3 list-none"
          >
            <ConversationChannelChip
              :inbox-id="message.inboxId"
              :conversation-id="message.conversationId"
              @open="openConversation"
            />
          </li>
        </template>
      </MessageList>
      <div
        v-if="topMessage"
        class="absolute z-10 -translate-x-1/2 top-3 left-1/2"
      >
        <ConversationChannelChip
          :inbox-id="topMessage.inbox_id"
          :conversation-id="topMessage.conversation_id"
          @open="openConversation"
        />
      </div>
    </div>
    <TimelineComposer
      :contact-id="contact.id"
      :preferred-inbox-id="lastMessageInboxId"
      :container-height="containerHeight"
      @update:conversation="activeConversation = $event"
    />
  </div>
</template>
